import React from 'react';
import { PageSectionProps } from './types';
import { isProdEnvironment } from 'utils/environment';

/**
 * Slim PageSection wrapper that renders pre-computed children. Used by the v2
 * experimentation pipeline via moduleRegistry, where children are always
 * materialized by the caller.
 *
 * This intentionally does NOT import ModuleCollectionRenderer. That import
 * lives on the full `./PageSection`, which the legacy render path still uses
 * for its own-fallback behavior. Keeping the shell dependency-free breaks the
 * moduleRegistry → PageSection → ModuleCollectionRenderer → ModuleRenderer →
 * moduleRegistry cycle at its source.
 *
 * Contract: callers must pre-render children. Rendering with no children
 * produces an empty <div> — a broken section. We fail loud pre-prod so drift
 * is caught before release; in prod we warn (captured by Datadog RUM) and
 * still render so users see a mostly-working page instead of an error state.
 */
const PageSectionShell = (props: PageSectionProps) => {
  if (props.children == null) {
    const message =
      'PageSectionShell rendered without children. ' +
      'The v2 pipeline must pre-render <ModuleCollectionRenderer> as children before ' +
      'calling this component. See PipelineV2Renderer for the canonical pattern.';

    if (!isProdEnvironment()) {
      throw new Error(message);
    }
    console.warn(message);
  }

  return <div data-testid="pageSection">{props.children}</div>;
};

export default PageSectionShell;
